import * as React from "react";

import { Picker, type PickerOption, type PickerProps } from "./picker";

/**
 * @deprecated Prefer `Picker` in new code (`docs/picker-taxonomy.md` §6.3).
 *
 * `Combobox` is now a thin adapter over `Picker`, kept only so that the twelve
 * existing `Combobox` / `MemberCombobox` / `RoleSelector` call sites do not
 * have to move in the same change. Its name, its types and every one of its
 * props still work exactly as before; only its defaults differ from `Picker`'s
 * ("Select option..." vs "Select…", 300px vs 320px), and those are preserved
 * here so no call site's copy changes.
 *
 * Re-hosting it fixes, for all twelve at once:
 *
 * - `role="combobox"` on a button with no `aria-controls`,
 *   `aria-activedescendant` or `aria-autocomplete`, and a search `<input>`
 *   associated with nothing;
 * - `tabIndex={0}` on every `role="option"` — a 200-option list was 200 tab
 *   stops;
 * - no `↑`/`↓`, no typeahead, no `Home`/`End`;
 * - focus not entering the search input when the panel opened;
 * - group headings surviving an empty filtered set;
 * - single-select **clearing on re-selection** — confirming the current value
 *   deleted it;
 * - `ChevronsUpDown`, the glyph that made two different mechanisms look like
 *   one control.
 */
export type ComboboxOption = PickerOption;

/** @deprecated Prefer `PickerProps`. */
export type ComboboxProps = PickerProps;

const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  function Combobox(
    {
      placeholder = "Select option...",
      searchPlaceholder = "Search...",
      emptyMessage = "No options found.",
      maxHeight = 300,
      ...props
    },
    ref,
  ) {
    return (
      <Picker
        ref={ref}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        maxHeight={maxHeight}
        {...props}
      />
    );
  },
);
Combobox.displayName = "Combobox";

export { Combobox };
